import {
  Link as ReactRouterLink,
  type LinkProps,
  Navigate as ReactRouterNavigate,
  type NavigateOptions,
  type NavigateProps,
  NavLink as ReactRouterNavLink,
  type NavLinkProps,
  type RelativeRoutingType,
  type To,
  useLinkClickHandler as useReactRouterLinkClickHandler,
  useNavigate as useReactRouterNavigate,
  useSearchParams as useReactRouterSearchParams,
} from '@github-ui/react-router'
import {useCallback} from 'react'

function stateWithDefaults(state?: any) {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return {skipTurbo: true, ...state}
}
/**
 * Wraps @github-ui/react-router's useNavigate hook to add a skipTurbo state property
 */
export const useNavigate = () => {
  const navigate = useReactRouterNavigate()

  return useCallback(
    (to: To, opts?: NavigateOptions): void | Promise<void> => {
      return navigate(to, {
        ...opts,
        state: stateWithDefaults(opts?.state),
      })
    },
    [navigate],
  )
}

/**
 * Wraps @github-ui/react-router's Link component to add a skipTurbo state property
 */
export function Link({ref, ...props}: LinkProps & {ref?: React.Ref<HTMLAnchorElement>}) {
  return <ReactRouterLink data-turbo="false" {...props} state={stateWithDefaults(props.state)} ref={ref} />
}

/**
 * Wraps @github-ui/react-router's NavLink component to add a skipTurbo state property
 */
export function NavLink({ref, ...props}: NavLinkProps & {ref?: React.Ref<HTMLAnchorElement>}) {
  return <ReactRouterNavLink data-turbo="false" {...props} state={stateWithDefaults(props.state)} ref={ref} />
}

export const useSearchParams = () => {
  const [searchParams, setSearchParams] = useReactRouterSearchParams()

  const setUrlSearchParams: typeof setSearchParams = useCallback(
    (nextParams, opts) => {
      setSearchParams(nextParams, {
        ...opts,
        state: stateWithDefaults(opts?.state),
      })
    },
    [setSearchParams],
  )

  return [searchParams, setUrlSearchParams] as const
}

export function Navigate(props: NavigateProps) {
  return <ReactRouterNavigate {...props} state={stateWithDefaults(props.state)} />
}

export function useLinkClickHandler<E extends Element = HTMLAnchorElement>(
  to: To,
  opts?: {
    target?: React.HTMLAttributeAnchorTarget
    replace?: boolean
    state?: any
    preventScrollReset?: boolean
    relative?: RelativeRoutingType
  },
): (event: React.MouseEvent<E>) => void {
  const options = {
    ...opts,
    state: stateWithDefaults(opts?.state),
  }
  return useReactRouterLinkClickHandler<E>(to, options)
}

try{ Link.displayName ||= 'Link' } catch {}
try{ NavLink.displayName ||= 'NavLink' } catch {}
try{ Navigate.displayName ||= 'Navigate' } catch {}